import * as filestack from 'filestack-js';
import { PickerDisplayMode } from 'filestack-js';
import { IMAGE_PLACEHOLDER } from '../../../app/Constants/Filestack';

// TODO: get types working throughout this file

/** These need to be valid FileStack picker options */
interface FilestackUIOptions {
  container: HTMLElement;
  maxFiles?: number;
  displayMode: PickerDisplayMode;
  // Other filestack client options
  [key: string]: any;
}

interface FilestackUIHandlers {
  // TODO: Add types for these
  onSuccess: (res: any) => Promise<any>;
  onDelete: (file: any) => Promise<boolean>;
}

type UIMode = 'THUMBNAILS' | 'LIST';

/**
 * FilestackPickerUI
 * A UI library for Filestack pickers.
 * Handles rendering the list of files, and adding/removing files from the list.
 * Can also fire off callbacks when files are added/removed.
 */
export default class FilestackPickerUI {
  private client: filestack.Client;
  private picker: filestack.PickerInstance | null = null;
  private uiMode: UIMode;
  private previewElem: HTMLElement | null = null;
  private previewPlaceholder: string = IMAGE_PLACEHOLDER;
  private filestackOptions: FilestackUIOptions;
  private handlers: FilestackUIHandlers;
  private ol: HTMLOListElement;
  private checkbox: HTMLInputElement;
  private addImageListItem: HTMLLIElement;
  private fieldName: string;
  private label: string;
  private required: boolean;

  /**
   * @param options
   * @param options.apiKey - Filestack API key
   * @param options.uiMode - UI mode. Either 'THUMBNAILS' or 'LIST'
   * @param options.previewElem - Optional. An element to update the src of when the first image is added/changed.
   * @param options.filestackOptions - Filestack picker options. See https://filestack.github.io/filestack-js/interfaces/pickeroptions.html
   * @param options.handlers - Callbacks for when files are added/removed
   * @param options.label - Optional. Label for the file input. Defaults to "Add photo".
   * @param options.required - Optional. Whether file input is required. Defaults to false.
   */
  constructor(options: {
    apiKey: string,
    uiMode: UIMode,
    previewElem?: string | HTMLElement,
    previewPlaceholder?: string,
    filestackOptions: FilestackUIOptions,
    handlers: FilestackUIHandlers,
    label?: string,
    required?: boolean,
  }) {
    this.client = filestack.init(options.apiKey);
    this.uiMode = options.uiMode;
    this.filestackOptions = options.filestackOptions;
    this.handlers = options.handlers;
    if (options.previewElem) {
      this.previewElem = typeof options.previewElem === 'string' ?
        document.querySelector(options.previewElem) :
        options.previewElem;
    }
    this.previewPlaceholder = options.previewPlaceholder || IMAGE_PLACEHOLDER;
    this.fieldName = this.filestackOptions.container.dataset.name || 'media';
    this.label = options.label || 'Add photo';
    this.required = options.required || false;
  }

  /** Init the UI library. Creates the list and items etc. */
  public async init() {
    // Create the list
    this.ol = document.createElement('ol');
    this.ol.classList.add('filestack-picker__list');
    if (this.uiMode === 'THUMBNAILS') {
      this.ol.classList.add('filestack-picker__list--thumbnails');
    }
    // Create the checkbox for if file(s) have been uploaded
    this.checkbox = document.createElement('input');
    this.checkbox.type = 'checkbox';
    this.checkbox.title = 'Files uploaded';
    this.checkbox.name = this.fieldName + '_files_uploaded';
    this.checkbox.checked = false;
    this.checkbox.required = this.required;
    this.checkbox.style.display = 'none'; // Hide the checkbox
    // Add to DOM
    this.filestackOptions.container.appendChild(this.checkbox);
    this.filestackOptions.container.appendChild(this.ol);
    await this.renderAddButton();
    await this.renderPreloadedFiles();

    // Add mutation observer to the list and update preview image src when modified
    if (this.previewElem) {
      const listObserver = new MutationObserver(() => {
        const li = this.ol.children[0];
        const img = li.querySelector('img');
        const src = img?.src || this.previewPlaceholder;
        this.previewElem?.setAttribute('src', src);
      });
      listObserver.observe(this.ol, {
        childList: true,
      });
    }
  }

  /** Render the "add image" button */
  private async renderAddButton() {
    const li = document.createElement('li');
    li.classList.add('filestack-picker__list-item');

    // Add the button if we're not in dropPane mode
    if (this.filestackOptions.displayMode !== PickerDisplayMode.dropPane) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = this.label;
      button.classList.add('filestack-picker__add-button');
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        await this.launchPicker();
      });
      li.appendChild(button);
    }

    // Add the list item to the list
    this.ol.appendChild(li);
    this.addImageListItem = li;

    // If in dropPane mode, the "button" is the dropPane itself
    if (this.filestackOptions.displayMode === PickerDisplayMode.dropPane) {
      await this.launchPicker();
    }
  }

  /** Show/hide the "add new image" button if we've reached the max number of images */
  private async refreshAddButtonVisibility() {
    if (this.filestackOptions.maxFiles && this.ol.children.length - 1 >= this.filestackOptions.maxFiles) {
      this.addImageListItem.style.display = 'none';
    } else {
      this.addImageListItem.style.display = '';
    }

    // Relaunch the dropPane if we're in dropPane mode, to enforce the max files limit
    if (this.filestackOptions.displayMode === PickerDisplayMode.dropPane) {
      await this.launchPicker();
    }
  }

  /** Render out any files from data-preload attribute JSON */
  private async renderPreloadedFiles() {
    const preloadedFiles = this.filestackOptions.container.dataset.preload;
    if (!preloadedFiles) {
      return;
    }
    const files = JSON.parse(preloadedFiles);
    for (const file of files) {
      const li = this.createFileListItem(file);
      this.ol.insertBefore(li, this.ol.lastChild);
    }

    await this.refreshAddButtonVisibility();
  }

  /** Launch the Filestack picker */
  private async launchPicker() {
    // If we already have a picker instance, destroy it
    if (this.picker) {
      await this.picker.close();
      this.picker = null;
      // console.debug('picker closed');
    }

    // Construct the picker options
    const pickerOptions = {
      ...this.filestackOptions,
    };

    // Make each new picker instance only allow the number of files needed to fill the maxFiles limit
    if (this.filestackOptions.maxFiles) {
      const filesRemaining = this.filestackOptions.maxFiles - this.ol.children.length + 1;
      // console.debug('filesRemaining', filesRemaining);
      if (filesRemaining > 0) { // Filestack will throw an error if maxFiles is 0
        pickerOptions.maxFiles = filesRemaining;
      }
    }

    // If it's a dropPane, we set the success handler in the dropPane options
    // https://filestack.github.io/filestack-js/interfaces/pickerdroppaneoptions.html
    if (this.filestackOptions.displayMode === PickerDisplayMode.dropPane) {
      pickerOptions.container = this.addImageListItem; // Render dropPane in the list item, not in the main container
      pickerOptions.dropPane = {
        ...this.filestackOptions.dropPane,
        // Don't allow full screen drag and drop, since it interferes with the photo FilePickerUI instance
        overlay: false,
        onSuccess: (res) => {
          console.debug('dropPane success', res);
          return this.handleUploadDone(res);
        },
      };
    } else {
      // Otherwise, we set the success handler in the picker options
      pickerOptions.onUploadDone = (res) => {
        console.debug('picker success', res);
        return this.handleUploadDone(res);
      };
    }

    // Update our picker instance
    this.picker = this.client.picker(pickerOptions);

    // Open the picker
    await this.picker.open();
  }

  /** Create an LI with a preview, hidden input with the filestack data, and a delete button */
  private createFileListItem(file: any) {
    const li = document.createElement('li');
    li.classList.add('filestack-picker__list-item');
    li.dataset.handle = file.handle;

    // If we're in thumbnail mode, add an image preview
    if (this.uiMode === 'THUMBNAILS') {
      const img = document.createElement('img');
      img.classList.add('filestack-picker__thumbnail');
      img.src = file.url;
      img.alt = file.filename;
      li.appendChild(img);
    } else if (this.uiMode === 'LIST') {
      // If in list mode, add a span with the filename
      const span = document.createElement('span');
      span.classList.add('filestack-picker__filename');
      span.textContent = file.filename;
      li.appendChild(span);
    }

    // Add hidden input with filestack data
    const jsonInput = document.createElement('input');
    jsonInput.type = 'hidden';
    jsonInput.name = `${this.fieldName}[]`; // The [] makes it an array
    jsonInput.value = JSON.stringify(file);

    // Add delete button
    const button = document.createElement('button');
    button.type = 'button';
    button.title = 'Delete file';
    button.classList.add('filestack-picker__delete-button');
    button.addEventListener('click', (e) => {
      e.preventDefault();
      this.handleRemove(file);
    });

    // Append content to li
    li.appendChild(jsonInput);
    li.appendChild(button);

    return li;
  }

  /** Add a preview and fire off the onSuccess handler for each image */
  private async handleUploadDone(res) {
    // Drop pane just returns an array, not a response object
    const files = res.filesUploaded || res;
    for (const file of files) {
      const li = this.createFileListItem(file);
      this.ol.insertBefore(li, this.ol.lastChild);
      this.handlers.onSuccess(file);
    }

    // Check the checkbox since we have files
    this.checkbox.checked = true;

    await this.refreshAddButtonVisibility();
  }

  /**
   * Event handler for when the x button is clicked.
   * Will execute the `onDelete` callback if present, and remove the file from the UI if successful.
   */
  private async handleRemove(file: any) { // TODO: types
    // Run handler if present
    if (this.handlers.onDelete) {
      // TODO: Add spinner animation in the future maybe?
      const success = await this.handlers.onDelete(file);
      console.debug('onDelete success:', success);
      if (!success) {
        return;
      }
    }

    // Remove from UI
    const li = Array.from(this.ol.children).find((li: HTMLLIElement) => {
      return li.dataset.handle === file.handle;
    });
    if (li) {
      li.remove();
    }

    // If no files left, uncheck the checkbox
    if (this.ol.children.length === 1) {
      this.checkbox.checked = false;
    }

    await this.refreshAddButtonVisibility();
  }

  /**
   * Reset the widget by removing all uploaded files.
   * @param deleteFiles - If true, runs the onDelete handler for all files (i.e. to delete from Filestack).
   */
  public async reset(deleteFiles = false) {
    const files = Array.from(this.ol.children).slice(0, -1); // Exclude the add button list item
    for (const li of files) {
      const file = JSON.parse((li.querySelector('input[type="hidden"]') as HTMLInputElement).value);
      if (deleteFiles && this.handlers.onDelete) {
        await this.handlers.onDelete(file);
      }
      li.remove();
    }
    this.checkbox.checked = false;
    await this.refreshAddButtonVisibility();
  }
}
